import { FlexLayout, Typography } from '@eduact/ed-system';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { IMrqAnswer, Options, Test } from '../TestSummary.types';
import { QuestionContentWrapper, Text } from '../OrderingAnswer/OrderingAnswer';
import { FeedbacktWrapper } from '../GapAnswer/GapAnswer';
import { Spacer } from '@Styled/utilities';
import { Color, Colors } from '@Styled/theme';

type MrqProps = {
	question: IMrqAnswer;
	test: Test | undefined;
	status: string | undefined;
};

/**
 * mrq answers never rendered here at all — the summary had no component (and
 * until the backend fix, no data) for them. Matching is per stored entry,
 * id-first: ids identify options, texts are display snapshots; compare ids
 * only when BOTH sides carry one (undefined === undefined is true and would
 * mark every option chosen). Entries whose option was edited away or deleted
 * render as orphan rows from the snapshot text.
 */
const MrqAnswer: React.FC<MrqProps> = ({ question, test, status }) => {
	const showAnswer = useMemo(() => {
		return (
			(status === 'failed' && test?.show_correct_if_failed === true) ||
			(status === 'passed' && test?.show_correct_if_passed === true)
		);
	}, []);

	// Array.isArray guard: a question type-flipped from mcq leaves OBJECT-
	// shaped content behind, and .find/.filter on it throws at render.
	const rawStored = question?.answer?.content?.options;
	const storedEntries = Array.isArray(rawStored) ? rawStored : [];

	const chosenEntryFor = (item: Options) =>
		storedEntries.find((entry) => {
			if (entry.option_id != null && item.id != null)
				return entry.option_id === item.id;
			return entry.answer === item.choice;
		});

	const getMcqColor = (item: Options) => {
		const chosenOption = chosenEntryFor(item);
		if (
			(!showAnswer && chosenOption?.correct === false) ||
			(chosenOption && !chosenOption.correct)
		)
			return '#ffd5cc';
		if (!showAnswer && chosenOption?.correct) return '#aeaeae19';
		if (showAnswer && chosenOption?.correct) return '#00d66b19';
		if (showAnswer && item.is_correct) return '#00a05056';
		return '#FFF';
	};
	const bulletColor = (item: Options): Color => {
		const chosenOption = chosenEntryFor(item);
		if (
			(!showAnswer && chosenOption?.correct === false) ||
			(chosenOption && !chosenOption.correct)
		)
			return 'lightRed';
		if (!showAnswer && chosenOption?.correct) return 'silver';
		if (showAnswer && chosenOption?.correct) return 'green';
		if (showAnswer && item.is_correct) return 'green';
		return 'silver';
	};
	const bulletState = (item: Options) => !!chosenEntryFor(item);

	const liveOptions = question?.multipleChoiceOptions ?? [];
	const orphans = storedEntries.filter(
		(entry) =>
			!liveOptions.some((item) => {
				if (entry.option_id != null && item.id != null)
					return entry.option_id === item.id;
				return entry.answer === item.choice;
			})
	);

	return (
		<Spacer p={{ sm: '1rem' }}>
			<QuestionContentWrapper
				dangerouslySetInnerHTML={{ __html: question.content }}
			/>
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout px={{ sm: '0.813rem' }} flexDirection="column">
				<AnswersLabel
					fontSize={{ sm: '0.5rem', lg: '1rem' }}
					fontFamily="Roboto"
					color="#000000, 0.6"
				>
					Answers
				</AnswersLabel>
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				<FlexLayout flexDirection={'column'}>
					{liveOptions.map((mcqItem, index) => {
						return (
							<FlexLayoutStyle
								alignItems={'center'}
								key={`${mcqItem.id ?? mcqItem.choice}-${index}`}
								mb={{ sm: '0.75rem' }}
								background={getMcqColor(mcqItem)}
							>
								<AnswerBullet
									filled={bulletState(mcqItem)}
									state={bulletColor(mcqItem)}
								/>
								<Spacer mx={{ sm: '4px' }} />
								<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
									<Label htmlFor={mcqItem.choice}>{mcqItem.choice}</Label>
								</Typography>
							</FlexLayoutStyle>
						);
					})}
					{orphans.map((entry, index) => (
						<FlexLayoutStyle
							alignItems={'center'}
							key={`orphan-${entry.option_id ?? entry.answer}-${index}`}
							mb={{ sm: '0.75rem' }}
							background={entry.correct ? '#00d66b19' : '#ffd5cc'}
						>
							<AnswerBullet filled state={entry.correct ? 'green' : 'lightRed'} />
							<Spacer mx={{ sm: '4px' }} />
							<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<Label>
									{entry.answer} — student's answer (option no longer exists)
								</Label>
							</Typography>
						</FlexLayoutStyle>
					))}
				</FlexLayout>
			</FlexLayout>
			{question.feedback !== null && (
				<>
					<Text>Feedback</Text>
					<FeedbacktWrapper
						dangerouslySetInnerHTML={{ __html: question.feedback as string }}
					/>
				</>
			)}
		</Spacer>
	);
};

export default MrqAnswer;

const FlexLayoutStyle = styled(FlexLayout)<{ background: string }>`
	background: ${({ background }) => background};
	height: 2.313rem;
	padding: 0.5rem;
	border-radius: 7px;
	input[type='radio']#correct {
		accent-color: #00d66b;
	}
	input[type='radio']#wrong {
		accent-color: #ff0000;
	}
`;
const Label = styled.label`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.87);
	font-weight: normal;
	@media screen and (min-width: 769px) {
		font-size: 1.125rem;
	}
`;
const AnswerBullet = styled.div<{ state: Color; filled: boolean }>`
	border-radius: 50%;
	position: relative;
	background: ${({ filled, state }) => (filled ? Colors[state] : '')};
	box-shadow: inset 0 0 0 1px #fff;
	outline: 1px solid ${({ filled, state }) => (filled ? Colors[state] : 'gray')};
	width: 0.535rem;
	height: 0.535rem;
	@media screen and (min-width: 769px) {
		width: 1.25rem;
		height: 1.25rem;
		box-shadow: inset 0 0 0 3px #fff;
	}
`;
const AnswersLabel = styled(Typography)`
	color: '#c4c4c4';
	font-size: 1rem;
	font-weight: 300;
	@media screen and (min-width: 769px) {
		margin-bottom: 0.75rem;
	}
`;
